import React, { useMemo } from 'react';
import { Line } from 'react-konva';
import { useGrid } from '../GridProvider/useGrid';
import { calculateGridContours } from '../../features/settings/utils/contourTracer';
import type { MapRegion, RenderedImageRect } from '../../types/types';
import type { Stage } from 'konva/lib/Stage';

interface RegionBordersLayerProps {
  imageRect: RenderedImageRect | null;
  stageRef: React.RefObject<Stage | null>;
  mapRegions: MapRegion[];
  activeMapId: number | null;
  showMapRegions: boolean;
}

const RegionBordersLayer: React.FC<RegionBordersLayerProps> = ({ imageRect, mapRegions, activeMapId, showMapRegions }) => {
  // useGrid is a React context hook -- allowed in presenters per ADR-034
  const { gridSettings, gridStrategy, isGridReady } = useGrid();

  const regionOutlines = useMemo(() => {
    if (!showMapRegions || !gridSettings.enabled || !gridStrategy || !isGridReady || !imageRect) return [];

    const activeRegions = (mapRegions ?? []).filter((region: MapRegion) =>
      region.mapId === activeMapId &&
      region.visible !== false &&
      region.type === gridSettings.type
    );

    const gridInstance = gridStrategy.getContourGrid();

    if (!gridInstance) return [];

    return (activeRegions ?? []).flatMap((region) => {
      if (!Array.isArray(region.cells)) {
        return [];
      }
      // The contour function expects cells as an array of {row, col} objects.
      const cells = region.cells as { row: number, col: number }[];
      const contours = calculateGridContours(cells, gridInstance);

      return (contours ?? []).map((contour: [number, number][], index: number) => (
        <Line
          key={`${region.id}-contour-${index}`}
          points={(contour ?? []).flat()}
          stroke={region.color ?? '#FFFFFF'}
          strokeWidth={2}
          closed={true}
          listening={false}
        />
      ));
    });
  }, [mapRegions, activeMapId, gridSettings, showMapRegions, gridStrategy, isGridReady, imageRect]);

  return <>{regionOutlines}</>;
};

export default RegionBordersLayer;
